import { useState, useEffect } from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import FountainRenderer from './FountainRenderer';
import './DocumentViewer.css';

const DocumentViewer = ({ document, onClose, documents, onNavigate }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [toc, setToc] = useState([]);

  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);
      try {
        const text = await document.file.text();
        setContent(text);
        
        // Extract table of contents from headings
        if (document.type === 'md' || document.type === 'markdown') {
          const headings = text.match(/^#{1,6}\s+.+$/gm) || [];
          const tocItems = headings.map((heading, index) => {
            const level = heading.match(/^#+/)[0].length;
            const title = heading.replace(/^#+\s+/, '');
            const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return { level, title, id, index };
          });
          setToc(tocItems);
        }
      } catch (error) {
        console.error('Error loading document:', error);
      }
      setLoading(false);
    };

    loadContent();
  }, [document]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner">◆</div>
          <div className="loading-text">Loading document...</div>
        </div>
      );
    }

    if (document.type === 'fountain') {
      return <FountainRenderer content={content} />;
    } else if (document.type === 'md' || document.type === 'markdown') {
      return <MarkdownRenderer content={content} documents={documents} onNavigate={onNavigate} />;
    } else if (document.type === 'html' || document.type === 'htm') {
      return (
        <div 
          className="html-content"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    return <div className="unsupported-format">Unsupported file format</div>;
  };

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="document-viewer">
      <div className="document-header">
        <button className="close-btn" onClick={onClose}>
          ← Back
        </button>
        <div className="document-info">
          <h1 className="document-title">
            {document.name.replace(/\.(md|markdown|html|htm|fountain)$/, '')}
          </h1>
          <div className="document-meta">
            <span className="meta-item">
              {new Date(document.uploadDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="meta-divider">•</span>
            <span className="meta-item">{document.type.toUpperCase()}</span>
          </div>
        </div>
      </div>

      <div className="document-body">
        {toc.length > 0 && (
          <aside className="document-sidebar">
            <div className="sidebar-title">Contents</div>
            <nav className="table-of-contents">
              {toc.map((item) => (
                <a
                  key={item.index}
                  className={`toc-item toc-level-${item.level}`}
                  onClick={() => scrollToHeading(item.id)}
                  href={`#${item.id}`}
                >
                  {item.title}
                </a>
              ))}
            </nav>
          </aside>
        )}

        <div className={`document-content ${toc.length > 0 ? 'with-sidebar' : ''}`}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
